import type { CommerceConfig } from '../site/config'
import type { ComparisonResult, CompressorRecord, ToolRecord } from '../utilities/compressor/compare'
import { pluralizeToolType } from '../utilities/pluralize-tool-type.ts'

/**
 * Retailer links under a matcher result. Ansh's rule (2026-09-24): plain shopping links, no
 * verdict and no per-suggestion calculation. "combo" when the compressor's published output
 * meets or exceeds the tool's published demand at the same PSI; "options" for every other
 * state, including not-comparable and missing-figure results.
 */
export type CommerceKind = 'combo' | 'options'

export type CommerceLink = {
  item: 'tool' | 'compressor'
  itemId: string
  label: string
  href: string
}

export type CommerceBlock = {
  kind: CommerceKind
  heading: string
  links: CommerceLink[]
  disclosure: string
}

export function commerceKind(result: ComparisonResult): CommerceKind {
  return result.status === 'comparable' && result.difference >= 0 ? 'combo' : 'options'
}

export function retailerSearchUrl(config: CommerceConfig, query: string): string {
  const url = new URL(config.searchUrl.replace('{query}', encodeURIComponent(query)))
  if (config.trackingId) url.searchParams.set(config.trackingParam, config.trackingId)
  url.search = url.searchParams.toString()
  return url.toString()
}

export function commerceBlock(
  config: CommerceConfig,
  result: ComparisonResult,
  tool: ToolRecord,
  compressor: CompressorRecord,
): CommerceBlock | null {
  if (!config.enabled) return null

  const kind = commerceKind(result)
  const toolName = `${tool.brand} ${tool.model}`
  const compressorName = `${compressor.brand} ${compressor.model}`
  const links: CommerceLink[] = kind === 'combo'
    ? [
        { item: 'tool', itemId: tool.id, label: toolName, href: retailerSearchUrl(config, toolName) },
        { item: 'compressor', itemId: compressor.id, label: compressorName, href: retailerSearchUrl(config, compressorName) },
      ]
    : [
        { item: 'compressor', itemId: 'other-compressors', label: 'Other air compressors', href: retailerSearchUrl(config, 'air compressor') },
        {
          item: 'tool',
          itemId: 'other-tools',
          label: `Other ${pluralizeToolType(tool.toolType, 2).toLowerCase()}`,
          href: retailerSearchUrl(config, `pneumatic ${tool.toolType.toLowerCase()}`),
        },
      ]

  return {
    kind,
    heading: kind === 'combo' ? `Get this combo on ${config.retailer}` : `Check other options on ${config.retailer}`,
    links,
    disclosure: config.disclosure,
  }
}

export function affiliateClickEvent(block: CommerceBlock, link: CommerceLink, retailer: string) {
  return {
    name: 'affiliate_link_clicked',
    details: { placement: 'result', kind: block.kind, item: link.item, item_id: link.itemId, retailer },
  }
}
